// Sincronização morada↔rota (Fase 2) — recalcula a geometria de uma rota
// depois de o local de serviço de uma das suas OT ter mudado, MANTENDO a
// ordem das paragens.
//
// A geometria é calculada no browser (HERE), tal como no planeamento; o
// resultado é persistido via POST /routes/:id/recompute-geometry, que
// emite `route:updated` para o painel e para a app do motorista.

import { calculateRouteHERE } from './hereApi';
import { findBestOrder } from './consolidationValidator';

const API = '/planner-api';

// Distância em metros entre dois pontos {lat,lng}.
function haversineM(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const x = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

/**
 * @param {object} p
 * @param {number} p.routeId  rota a recalcular
 * @param {number} p.otId     OT cujo local de serviço mudou
 * @param {number} p.lat      nova latitude
 * @param {number} p.lng      nova longitude
 * @param {string} [p.name]   novo nome/morada da paragem
 * @param {string} p.token    JWT
 * @returns {Promise<{ok:boolean, error?:string, notFound?:boolean}>}
 */
export async function recomputeRouteAfterOTChange({ routeId, otId, lat, lng, name, token }) {
  if (!routeId || !otId || !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) {
    return { ok: false, error: 'Parâmetros inválidos para o recálculo.' };
  }

  // 1. Estado actual da rota.
  let route;
  try {
    const res = await fetch(`${API}/routes/${routeId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return { ok: false, error: 'Não foi possível ler a rota.' };
    route = await res.json();
  } catch {
    return { ok: false, error: 'Erro de ligação ao ler a rota.' };
  }

  const origin = route.origin ? { ...route.origin } : null;
  const destination = route.destination ? { ...route.destination } : null;
  const waypoints = Array.isArray(route.waypoints)
    ? route.waypoints.map(w => ({ ...w }))
    : [];
  if (!origin?.coordinates || !destination?.coordinates) {
    return { ok: false, error: 'A rota não tem origem/destino definidos.' };
  }

  const newCoords = [Number(lng), Number(lat)]; // [lng,lat], formato GeoJSON

  // 2. Localiza a paragem da OT (destino, waypoint intermédio ou origem) e
  //    actualiza-lhe as coordenadas. Tanto o destino como os waypoints
  //    guardam `otId`.
  let found = false;
  if (Number(destination.otId) === Number(otId)) {
    destination.coordinates = newCoords;
    if (name) destination.name = name;
    found = true;
  } else {
    const idx = waypoints.findIndex(w => Number(w.otId) === Number(otId));
    if (idx >= 0) {
      waypoints[idx].coordinates = newCoords;
      if (name) waypoints[idx].name = name;
      found = true;
    } else if (Number(origin.otId) === Number(otId)) {
      origin.coordinates = newCoords;
      if (name) origin.name = name;
      found = true;
    }
  }
  if (!found) {
    return { ok: false, notFound: true, error: 'A OT não consta como paragem desta rota.' };
  }

  // 3. Recalcula a geometria (mesma ordem de paragens). Numa rota de
  //    ida-e-volta o destino real passa a ser a última paragem e o
  //    cálculo termina na origem.
  const isRoundTrip = !!(route.routeData?.roundTrip || route.roundTrip);
  const calcOrigin = origin.coordinates;
  const calcDest = isRoundTrip ? origin.coordinates : destination.coordinates;
  const viaCoords = waypoints
    .map(w => w.coordinates)
    .filter(c => Array.isArray(c) && c.length === 2);
  const calcVias = isRoundTrip ? [...viaCoords, destination.coordinates] : viaCoords;
  const options = { ...(route.routeData?.routeOptions || {}) };

  let calc;
  try {
    calc = await calculateRouteHERE(calcOrigin, calcDest, calcVias, options);
  } catch {
    return { ok: false, error: 'Falha ao recalcular a geometria (HERE).' };
  }
  const computed = calc?.route;
  if (!computed?.geometry) {
    return { ok: false, error: 'A HERE não devolveu uma geometria válida.' };
  }

  // 4. Persiste a nova geometria.
  try {
    const res = await fetch(`${API}/routes/${routeId}/recompute-geometry`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        origin, destination, waypoints,
        computedRoute: computed,
        totalDistanceM: computed.distance,
        totalDurationS: computed.duration,
        reason: 'ot-location-sync',
        changedOtId: Number(otId),
      }),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      return { ok: false, error: j.error || 'Falha ao gravar a rota recalculada.' };
    }
  } catch {
    return { ok: false, error: 'Erro de ligação ao gravar a rota.' };
  }

  return { ok: true };
}

// Fase 3 — re-optimiza uma rota EM TRÂNSITO depois de o local de serviço
// de uma OT mudar. As OTs já entregues ficam fixas no início; as que
// faltam (incluindo a editada) são reordenadas por vizinho-mais-próximo
// a partir do GPS actual do veículo. Recalcula a geometria e persiste.
//
// @returns {Promise<{ok:boolean, error?:string, notFound?:boolean, reordered?:number}>}
export async function reoptimizeRouteAfterOTChange({ routeId, otId, lat, lng, name, token }) {
  if (!routeId || !otId || !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) {
    return { ok: false, error: 'Parâmetros inválidos para a re-optimização.' };
  }

  // 1. Rota + contexto de re-planeamento (GPS actual + OTs entregues).
  let route, ctx;
  try {
    const [rRes, cRes] = await Promise.all([
      fetch(`${API}/routes/${routeId}`, { headers: { Authorization: `Bearer ${token}` } }),
      fetch(`${API}/routes/${routeId}/replan-context`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    if (!rRes.ok) return { ok: false, error: 'Não foi possível ler a rota.' };
    route = await rRes.json();
    ctx = cRes.ok ? await cRes.json() : {};
  } catch {
    return { ok: false, error: 'Erro de ligação ao ler a rota.' };
  }

  const origin = route.origin ? { ...route.origin } : null;
  if (!origin?.coordinates) return { ok: false, error: 'A rota não tem origem definida.' };

  const allStops = [
    ...(Array.isArray(route.waypoints) ? route.waypoints : []),
    ...(route.destination ? [route.destination] : []),
  ].map(s => ({ ...s }));
  if (allStops.length === 0) return { ok: false, error: 'A rota não tem paragens.' };

  // Actualiza a coordenada da OT editada.
  const newCoords = [Number(lng), Number(lat)];
  let found = false;
  for (const s of allStops) {
    if (Number(s.otId) === Number(otId)) {
      s.coordinates = newCoords;
      if (name) s.name = name;
      found = true;
    }
  }
  if (!found) return { ok: false, notFound: true, error: 'A OT não consta como paragem desta rota.' };

  // 2. Partição: entregues (fixas, mantêm a ordem) vs por entregar.
  const deliveredIds = new Set((ctx.deliveredOTs || []).map(o => Number(o.id)));
  const done = allStops.filter(s => deliveredIds.has(Number(s.otId)));
  const pending = allStops.filter(s => !deliveredIds.has(Number(s.otId)));
  const pendingOT = pending.filter(s => s.otId != null);
  const tail = pending.filter(s => s.otId == null); // depósito / regresso

  // 3. Reordena as por-entregar com o MESMO otimizador da criação de rotas
  //    (findBestOrder do consolidationValidator): respeita a janela horária
  //    (início E fim), o tempo de serviço e o escalão de SLA, com a distância
  //    como critério inferior. Antes era só vizinho-mais-próximo, que ignorava
  //    as janelas/serviço e divergia da ordem planeada. As OT já entregues
  //    ficam FIXAS (não entram no otimizador); a otimização ARRANCA da posição
  //    actual do veículo (GPS → última entregue → origem) via opts.driverHome.
  //    Os dados de janela/serviço vêm do replan-context (ctx.inTransitOTs); as
  //    coordenadas vêm do waypoint (já reflectem a edição acabada de gravar).
  const cur = (ctx.currentGPS && Number.isFinite(ctx.currentGPS.lat))
    ? { lat: ctx.currentGPS.lat, lng: ctx.currentGPS.lng }
    : (done.length
      ? { lat: done[done.length - 1].coordinates[1], lng: done[done.length - 1].coordinates[0] }
      : { lat: origin.coordinates[1], lng: origin.coordinates[0] });
  const otFor = (otId) => (ctx.inTransitOTs || []).find(x => Number(x.id) === Number(otId)) || {};
  let ordered = pendingOT;
  let replanConflicts = [];
  if (pendingOT.length > 1) {
    const planOrders = pendingOT.map(s => {
      const o = otFor(s.otId);
      return {
        id: s.otId,
        orderNumber: s.orderNumber || o.orderNumber,
        deliveryLat: s.coordinates[1],
        deliveryLng: s.coordinates[0],
        deliveryWindowFrom: o.deliveryWindowFrom || null,
        deliveryWindowTo: o.deliveryWindowTo || null,
        pickupWindowFrom: o.pickupWindowFrom || null,
        pickupWindowTo: o.pickupWindowTo || null,
        slaHours: o.slaHours,
        unloadingTime: o.unloadingTime,
        createdAt: o.createdAt || null,
        __stop: s,
      };
    });
    const best = findBestOrder(planOrders, {
      driverHome: cur,       // arranca na posição actual do veículo
      defaultLoadMin: 0,     // assistência/entrega: sem tempo de carga no arranque
      defaultUnloadMin: 30,
    });
    ordered = (best.reorderedOrders || planOrders).map(o => o.__stop);
    replanConflicts = Array.isArray(best.conflicts) ? best.conflicts : [];
  }

  const newOrder = [...done, ...ordered, ...tail];
  const waypoints = newOrder.slice(0, -1);
  const destination = newOrder[newOrder.length - 1];

  // 4. Recalcula a geometria.
  const isRoundTrip = !!(route.routeData?.roundTrip || route.roundTrip);
  const calcOrigin = origin.coordinates;
  const calcDest = isRoundTrip ? origin.coordinates : destination.coordinates;
  const viaCoords = waypoints.map(w => w.coordinates).filter(c => Array.isArray(c) && c.length === 2);
  const calcVias = isRoundTrip ? [...viaCoords, destination.coordinates] : viaCoords;

  let calc;
  try {
    calc = await calculateRouteHERE(calcOrigin, calcDest, calcVias, { ...(route.routeData?.routeOptions || {}) });
  } catch {
    return { ok: false, error: 'Falha ao recalcular a geometria (HERE).' };
  }
  const computed = calc?.route;
  if (!computed?.geometry) return { ok: false, error: 'A HERE não devolveu uma geometria válida.' };

  // 5. Persiste (o endpoint emite route:updated → a app do motorista refaz a rota).
  try {
    const res = await fetch(`${API}/routes/${routeId}/recompute-geometry`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        origin, destination, waypoints,
        computedRoute: computed,
        totalDistanceM: computed.distance,
        totalDurationS: computed.duration,
        reason: 'ot-reoptimize',
        changedOtId: Number(otId),
      }),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      return { ok: false, error: j.error || 'Falha ao gravar a rota re-optimizada.' };
    }
  } catch {
    return { ok: false, error: 'Erro de ligação ao gravar a rota.' };
  }

  return { ok: true, reordered: ordered.length, conflicts: replanConflicts };
}
